import lodashCloneDeep from 'lodash/fp/cloneDeep';
import anonymousUser from 'client/constants/anonymousUser';
import anonymousUtilityUser from 'client/constants/anonymousUtilityUser';
import { LEAD_STATUS_LIST } from 'common/leadConstants';

/**
 * Makes the initial client state for the Utility Profit branch.
 */
function makeUtilityProfitInitialState() {
  return {
    utilityUser: {
      ...anonymousUtilityUser,
    },
    utilityApiState: {},
    utilityPartners: {
      currentPartner: null,
      list: null,
      bySlug: {},
      referralEarnings: {},
      stripeAccountsByPartnerSlug: {},
      unlistedAddressesCountBySlug: {},
      revenueDetailsBySlug: {},
      byPartnerGroupId: {},
    },
    utilityActivations: {
      types: null,
      countByPartnerSlug: {},
      byShortId: {},
      byUtilityUserId: {},
      byAddressId: {},
      lazySignupShortId: null,
      countBySalesRep: {},
    },
    utilityActivationLinkRequests: {
      byId: {},
    },
    utilityPartnerAddresses: {
      recentlyUnlistedByPartnerId: {},
    },
    utilityProviders: {
      list: null,
      coverageByAddressId: {},
      coverageByRange: {},
      byAddressId: {},
      internetPlansByPartnerAddressId: {},
    },
    utilityPayouts: {
      pendingPayouts: {
        byPartnerSlug: {},
      },
    },
    utilityTypes: {
      list: null,
    },
    utilityDealers: {
      list: null,
    },
    utilitySalesReps: {
      list: null,
    },
    utilityDelayedReasons: {
      list: null,
    },
    utilityResidualRevenue: {
      showPromptByPartnerId: {},
    },
    utilityMetabaseCharts: {
      byPartnerId: {},
    },
  };
}

/**
 * Makes the initial client state.
 */
export default function makeInitialState() {
  return {
    currentUser: { ...anonymousUser },
    isTransitioning: false,
    pendingCommit: false,

    apiState: {},
    dataTables: {},
    pageMetadata: {},
    scrollPositions: {
      shouldRestoreScrollPosition: false,
    },
    cardState: {},

    onboarding: {
      currentStepNumber: 1,
      formData: {},
    },

    resetPasswordForm: {
      phone: '',
    },

    updateMoveInDate: {
      modalOpen: false,
      forceMoveInDateUpdate: false,
    },

    assignedApartmentLocator: null,

    roommateApplication: {
      count: null,
      roommates: [],
    },

    users: {
      byId: {},
    },

    household: {
      renterProfilesByUserId: {},
      rentersByUserId: {},
      applicationsById: {},
      applicationsByUserId: {},
      byId: {},
      byUserId: {},
      tenantAlertReportByUserId: {},
      tenantAlertApplicationById: {},
      depositFeesByListingId: {},
      buyersAgentResultsByListingId: {},
      householdListingsBy: {},
      renterQualificationsByListingId: {},
      listingsApplyingFor: {
        byHouseholdId: {},
        byHouseholdListingId: {},
      },
    },

    householdApplications: null,
    householdApplicationSubmissions: null,

    search: {
      filters: lodashCloneDeep(anonymousUser.searchFilters),
      resultsCount: null,
      resultsCountTimestamp: null,
      suggestedSearchFilters: null,
      selectedSuggestedSearchFilter: null,
    },

    recentSearchQueries: null,

    fuzzySearchListings: {
      searchTerm: null,
      latestSearchTermRetrieved: null,
      results: {
        listings: [],
        partners: [],
      },
    },

    preLeases: {
      byListingId: {},
    },

    listings: {
      count: 12,
      page: 0,
      total: 0,
      isFullyLoaded: false,
      viewMoreCount: null,
      viewMoreIndex: null,
      totalListingsCount: 0,
      records: [],
      source: 'list', // list, map
      enableAllListings: false,
      adminInfoById: {},
      redirectionById: {},
      propertyFeaturesById: {},
      shouldChargeWithdrawFee: {},
      weeklyLeads: {},
      dailyPastSevenDaysLeads: {},
      priceChanges: {},
    },

    listingsDataManager: {
      views: {},
      listings: [],
    },

    recommendedListings: {
      count: 3,
      page: 0,
      isFullyLoaded: false,
      records: [],
    },

    noResultsPinnedListings: null,

    listingsViewMode: 'list', // list, map
    listingPhotoResolution: 2, // Default: `2` (a.k.a. 2x). Change to `1` or `3` to change image resolutions.

    listingsMapView: {
      viewedListings: [],
      viewingListingId: null,
      showRedoSearch: false,
      showResultsMatched: false,
      mapChanged: false,
      mapZoomLevel: null,
      mapCenter: null,
      mapArea: null,
    },

    feedCategory: {
      byId: {},
    },

    listing: null,
    listingTourHealth: {},
    listingTourHealthList: {
      items: [],
      dataTableIsFullyLoaded: false,
    },
    listingLeadHealth: {},
    listingsEligibleForDiscounts: {
      byPartnerSlug: {},
    },
    listingLinksByListingId: {},
    listingStripeAccountsByListingId: {},
    partnerStripeAccountsBySlug: {},
    apartmentLinksByApartmentId: {},
    tourDatesUnavailable: {},
    availableTourDates: null,
    unavailableTourDates: [],

    requestShowingStateByListingId: {},

    tenantScreeningCriteria: {
      byPartnerManagementCompany: {},
      byListingId: {},
    },

    householdsApplyingToListingByListingId: {},

    tours: null,
    addToTourListing: null,
    tourAgents: null,
    householdAdminsList: null,
    apartmentLocators: null,
    homebaseEmployees: null,
    bookmarksByListingId: null,
    checkAvailabilityByListingId: null,
    newBookmarksCount: 0,
    showTourAddedToCartTooltip: false,
    shouldRequestRenterTourFeedback: true,
    askWantsToApplyToListing: null,

    selfScheduling: {
      selectedDay: null,
      selectedTimes: [],
    },

    agent: {
      tours: null,
      agentUserId: null,
      tourGuideReportsByTourId: null,
      listingShowingInfo: null,
    },

    drivers: {
      current: null,
      byMarketId: {},
    },

    jobOrders: {
      byId: {},
    },

    mapViewById: {},
    scheduledLeadToursCountByListingId: {},

    agentMapView: {
      listings: [],
      viewingListing: null,
      viewedListingIds: [],
      viewingTour: null,
      viewingRenter: null,
      viewingRenterSearchFilters: null,
      mapChanged: false,
      mapZoomLevel: 11,
      mapCenter: {
        lat: 30.170061,
        lng: -97.749222,
      },
      mapArea: null,
      availableHomesToTourNow: true,
    },

    apartmentLocator: {
      leaderboard: [],
      maxLeadsStatus: null,
      unseenFeedActivityCount: null,
      apartmentsList: null,
      lead: {
        user: null,
        status: null,
        activity: null,
      },
      apartmentLeasesByUserId: {},
      leadsUpdated: {},
      leadsByStatus: LEAD_STATUS_LIST.reduce((memo, leadStatus) => {
        return {
          ...memo,
          [leadStatus]: {
            shouldLoadMorePastMonths: true,
            userMoveInDatePastMonthsRange: [],
          },
        };
      }, {}),
      monthToDateStats: null,
      phoneDevice: {
        isReady: false,
        isConnected: false,
        errorMessage: null,
        number: null,
        startedClockTimestamp: null,
        clockTimestamp: null,
      },
    },

    locatorTours: {
      guestCardFloorPlanIds: [],
    },

    sunlocks: {
      list: null,
      byId: {},
    },

    codebox: {
      list: null,
    },

    admin: {
      user: null,
    },

    leases: {
      byListingId: {},
      draftInfo: {},
      configurationsByPartnerId: {},
      configurationsList: null,
      documentsByHouseholdListingId: {},
    },

    idVerifications: {
      byUserId: {},
    },

    concessions: {
      byListingId: {},
    },

    incomeVerifications: {
      byApplicationId: {},
    },

    markets: {
      byId: {},
      neighborhoodsByMarketId: {},
      listLoaded: false,
    },

    addresses: {
      byId: {},
    },

    media: {
      byListingId: {},
      byApartmentId: {},
    },

    accountManagers: {
      list: [],
      listWithPartners: null,
      byListingId: {},
    },

    leasingManagers: {
      listWithPartners: [],
      byListingId: {},
    },

    landlords: {
      byId: {},
    },

    partners: {
      list: null,
      byUserId: {},
      currentPartner: null,
      currentPartnerUser: null,
      householdsInterestedByListingIds: {},
      householdsApplyingByListingId: {},
      overviewStats: {},
      listingStats: {},
      bySlug: {},
      byId: {},
      byManagementCompanyName: {},
      listingsViewId: null,
      apartmentsViewId: null,
      marketingListingsViewId: null,
      marketingListingsByListingId: {},
      stripeAccountsByPartnerSlug: {},
      users: {
        listByPartnerId: {},
      },
      buyersAgentCommissionsByPartnerId: {},
      marketConfigurationByIds: {},
      flatLinedListingAlertViewedByPartnerId: {},
    },

    apartments: {
      list: null,
      byId: {},
      floorPlansByApartmentId: {},
      specialDealsByApartmentId: {},
      specialDealsByApartmentBuildingId: {},
      byBuildingId: {},
      apartmentBuildingsById: {},
    },

    floorPlans: {
      listByApartmentId: {},
      showUnitsByFloorPlanId: {},
    },

    propertyAttributes: {
      listByListingId: {},
      listBy: {},
    },

    breedRestrictions: {
      definitions: null,
      byListingId: {},
    },

    petBreeds: {
      byName: {},
    },

    buyersSideTourRequests: {
      byListingAndUserId: {},
    },

    paymentAccounts: {
      byUserId: {},
    },

    propertyManagementSoftware: {
      list: null,
    },

    helpCards: {
      list: {},
    },

    plaidLinkToken: null,

    assemblyLine: {
      byHouseholdListings: {},
    },

    hitlistTask: {
      list: {},
      callScripts: null,
    },

    partnerTasks: {
      byListingId: {},
      byPartnerId: {},
    },

    manualIdVerifications: {
      list: [],
    },

    metabaseCharts: {
      byPartnerId: {},
    },

    mobileApp: {
      isMobileApp: false,
      isPartnerApp: false,
      askedPushNotificationPermission: false,
      deviceRegistrationId: null,
      features: {},
      askForReview: false,
      askPushNotifications: false,
    },

    analytics: {
      screenId: null,
      screenIdHistory: [],
      pathHistory: [],
      analyticsSessionId: null,
      shouldTrackNewSession: false,
      isEnabled: false,
      requestIp: null,
      location: {
        country: null,
        region: null,
        city: null,
        zip: null,
        lattitude: null,
        longitude: null,
      },
      utmValues: {
        campaign: null,
        source: null,
        medium: null,
        content: null,
        term: null,
      },
      experiments: {
        activeVariants: {},
        config: {},
      },
      userAgent: {
        browserName: null,
        browserVersion: null,
        deviceType: null,
        deviceVendor: null,
        deviceModel: null,
        engineName: null,
        engineVersion: null,
        osName: null,
        osVersion: null,
        cpuArchitecture: null,
        userAgentString: null,
        isMobile: null,
        isTablet: null,
        isBrowser: null,
        isCrawler: null,
      },
      prequalifications: {
        byListingId: {},
      },
    },
    // Every property in this object will be passed to the initial frontend state from the server's environment automatically.
    env: {
      NODE_ENV: null,
      APP_URL: null,
      UTILITY_APP_URL: null,
      APP_REDIRECTION_DOMAIN_URL: null,
      GOOGLE_API_KEY: null,
      MAPBOX_ACCESS_TOKEN: null,
      STRIPE_API_KEY: null,
      UTILITY_STRIPE_API_KEY: null,
      SENTRY_BROWSER_KEY: null,
      FRONT_NUMBER_LISTING_INQUIRIES: null,
      COMMIT_HASH: null,
      VOUCHED_PUBLIC_API_KEY: null,
      TYPEFORM_ID_RENTER_LEASE_SIGNED: null,
      TYPEFORM_ID_VERIFICATION_OF_RENTAL: null,
      TYPEFORM_ID_VERIFICATION_OF_EMPLOYMENT: null,
      TYPEFORM_FORM_ID_RENTERS_INSURANCE_WAITLIST: null,
      FRONT_CHANNEL_LISTING_INQUIRIES_TEXTS: null,
      VERIFAST_API_URL: null,
      DATADOG_APP_ID: null,
      DATADOG_CLIENT_TOKEN: null,
      CIRCLECI: null,
    },

    // Utility Profit state.
    ...makeUtilityProfitInitialState(),
  };
}
